/**
 * Article rail "Fuel this session" — the compact Subscribe & Save / one-time
 * control.
 *
 * Deliberately not `<selling-plans-widget>` (assets/component-selling-plans.js):
 * that element resolves its root with `this.closest('product-info')` and then
 * dereferences `.price`, `.price__regular` and `.price__sale` inside it without
 * a guard, so it only survives inside a full PDP buy panel. The Liquid that
 * feeds this element follows snippets/component-purchase-options.liquid exactly
 * — same allocations, same money filters — and this does the three things the
 * rail actually needs.
 *
 * Selling plan selection is native: the frequency control IS the
 * `name="selling_plan"` select, so picking one-time only has to disable it. A
 * disabled control is not submitted, which is what keeps the plan out of the
 * cart without a second hidden input to keep in step.
 *
 * Adding to cart is handled by assets/cart.js, which delegates on every
 * `form[action*="/cart/add"]` submit. Nothing here touches the cart.
 */

if (!customElements.get('article-fuel')) {
  customElements.define(
    'article-fuel',
    class ArticleFuel extends HTMLElement {
      onModeChange = () => {
        this.sync();
      };

      onFrequencyChange = () => {
        this.updatePrice();
      };

      connectedCallback() {
        if (!this.select) {
          return;
        }

        for (const input of this.modeInputs) {
          input.addEventListener('change', this.onModeChange);
        }
        this.select.addEventListener('change', this.onFrequencyChange);
        this.sync();
      }

      disconnectedCallback() {
        for (const input of this.modeInputs) {
          input.removeEventListener('change', this.onModeChange);
        }
        this.select?.removeEventListener('change', this.onFrequencyChange);
      }

      get modeInputs() {
        return this.querySelectorAll('[data-article-fuel-mode]');
      }

      get selectedMode() {
        return this.querySelector('[data-article-fuel-mode]:checked');
      }

      get frequency() {
        return this.querySelector('[data-article-fuel-frequency]');
      }

      get select() {
        return this.querySelector('[data-article-fuel-select]');
      }

      get priceTarget() {
        return this.querySelector('[data-article-fuel-price]');
      }

      get subscribing() {
        return this.selectedMode?.value === 'subscription';
      }

      sync() {
        // A disabled control is not submitted, which is the whole mechanism:
        // one-time simply takes `selling_plan` out of the form.
        this.select.disabled = !this.subscribing;
        if (this.frequency) {
          this.frequency.hidden = !this.subscribing;
        }

        this.updatePrice();
      }

      updatePrice() {
        const target = this.priceTarget;
        if (!target) {
          return;
        }

        const price = this.subscribing ? this.select.options[this.select.selectedIndex]?.dataset.price : this.selectedMode?.dataset.price;

        if (price) {
          target.textContent = price;
        }
      }
    },
  );
}

/**
 * The frequency `<select>` above stays the real form control — it is what
 * `ArticleFuel` reads and disables — but it renders `hidden` here. This
 * layers a trigger button plus a `popover` listbox on top of it, so the
 * option panel can be styled (something a native `<select>`'s popup cannot
 * be). Popover, not absolute positioning: `.article-fuel` has
 * `overflow: clip` for its rounded card, which would clip an
 * absolutely-positioned panel — a top-layer popover renders outside that
 * entirely. Picking an option writes `select.value` and dispatches `change`,
 * which is all `ArticleFuel.onFrequencyChange` listens for.
 */
if (!customElements.get('article-fuel-select')) {
  customElements.define(
    'article-fuel-select',
    class ArticleFuelSelect extends HTMLElement {
      onListClick = (event) => {
        const option = event.target.closest('.article-fuel__select-option');
        if (!option) {
          return;
        }

        this.selectOption(option);
      };

      // Arrow keys roam a roving tabindex across the options; Enter/Space
      // pick the focused one. Escape is handled explicitly (not left to the
      // popover's native light-dismiss) so closing it also restores focus.
      onListKeydown = (event) => {
        const options = this.options;
        const currentIndex = options.indexOf(document.activeElement);

        switch (event.key) {
          case 'ArrowDown':
            event.preventDefault();
            this.focusOption(options[Math.min(currentIndex + 1, options.length - 1)]);
            break;
          case 'ArrowUp':
            event.preventDefault();
            this.focusOption(options[Math.max(currentIndex - 1, 0)]);
            break;
          case 'Home':
            event.preventDefault();
            this.focusOption(options[0]);
            break;
          case 'End':
            event.preventDefault();
            this.focusOption(options[options.length - 1]);
            break;
          case 'Enter':
          case ' ':
            event.preventDefault();
            if (currentIndex >= 0) {
              this.selectOption(options[currentIndex]);
            }
            break;
          case 'Escape':
            event.preventDefault();
            this.closeList();
            break;
        }
      };

      onListBeforeToggle = (event) => {
        if (event.newState !== 'open') {
          return;
        }

        if (this.select.disabled) {
          event.preventDefault();
          return;
        }

        this.positionList();
      };

      onListToggle = (event) => {
        const isOpen = (event.newState === 'open');
        this.trigger.setAttribute('aria-expanded', String(isOpen));

        if (isOpen) {
          window.addEventListener('scroll', this.onWindowScroll, {capture: true, passive: true});
          const selectedOption = this.options.find((option) => option.dataset.value === this.select.value);
          this.focusOption(selectedOption ?? this.options[0]);
        } else {
          window.removeEventListener('scroll', this.onWindowScroll, {capture: true});
        }
      };

      // A fixed-position popover doesn't track the trigger as the page
      // scrolls, so close it rather than let it drift away from the button.
      onWindowScroll = () => {
        this.closeList();
      };

      connectedCallback() {
        if (!this.select || !this.trigger || !this.list) {
          return;
        }

        this.list.addEventListener('click', this.onListClick);
        this.list.addEventListener('keydown', this.onListKeydown);
        this.list.addEventListener('beforetoggle', this.onListBeforeToggle);
        this.list.addEventListener('toggle', this.onListToggle);
        this.syncSelected();
      }

      disconnectedCallback() {
        window.removeEventListener('scroll', this.onWindowScroll, {capture: true});
      }

      get select() {
        return this.querySelector('select');
      }

      get trigger() {
        return this.querySelector('.article-fuel__select-trigger');
      }

      get triggerText() {
        return this.querySelector('.article-fuel__select-trigger-text');
      }

      get list() {
        return this.querySelector('.article-fuel__select-list');
      }

      get options() {
        return Array.from(this.list.querySelectorAll('.article-fuel__select-option'));
      }

      positionList() {
        const triggerRect = this.trigger.getBoundingClientRect();
        this.list.style.top = `${triggerRect.bottom + 4}px`;
        this.list.style.left = `${triggerRect.left}px`;
        this.list.style.width = `${triggerRect.width}px`;
      }

      selectOption(option) {
        this.select.value = option.dataset.value;
        this.syncSelected();
        this.closeList();
        this.select.dispatchEvent(new Event('change', {bubbles: true}));
      }

      // Hiding the popover can leave focus on document.body or an orphaned
      // option instead of returning it to the trigger — the Popover API
      // doesn't guarantee that on its own — so every programmatic close
      // (selection, Escape, scroll) goes through here rather than calling
      // list.hidePopover() directly, and unconditionally lands focus back
      // on the trigger.
      closeList() {
        this.list.hidePopover();
        if (document.activeElement !== this.trigger) {
          this.trigger.focus();
        }
      }

      // Roving tabindex: only the focused option is in the Tab order, so
      // Tab exits the listbox rather than cycling through every option.
      focusOption(option) {
        if (!option) {
          return;
        }

        for (const listOption of this.options) {
          listOption.tabIndex = -1;
        }

        option.tabIndex = 0;
        option.focus();
      }

      syncSelected() {
        const selectedOption = this.select.options[this.select.selectedIndex];
        this.triggerText.textContent = selectedOption?.textContent.trim() ?? '';

        for (const option of this.options) {
          option.setAttribute('aria-selected', String(option.dataset.value === this.select.value));
        }
      }
    },
  );
}
